import React, {useCallback, useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import styled from 'styled-components'
import {
  PlatformAndroidApiKey,
  PlatformAndroidToken,
  PlatformAttributes
} from '../../../common/types'
import {Button} from '../../../components/library/buttons/button'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {Select, TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../../components/library/list/index'
import {Modal} from '../../../components/library/modal/modal'
import {Tag} from '../../../components/library/tag/tag'
import {OverflowFooter} from '../../../components/overflow'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {getToken} from '../../../lib/api'
import {notify} from '../../../lib/bugsnag'
import {getApi} from '../../../lib/fetch'
import {useListLazy} from '../../../lib/jsonAPI'
import {useToast} from '../../../lib/toast'
import {omit} from '../../../lib/updaters'
import {ButtonGroup} from '../../card-builder/validation/common'
import {
  DeleteLink,
  IconHeading,
  Section,
  SectionContent,
  SectionInfo
} from '../components'
import {AndroidForm} from './android'
import {isAndroid, isIOS} from './helper'
import {IosForm} from './ios'
import {
  BaseNotificationPlatformCodec,
  NotificationPlatform,
  PlatformResponse
} from './types'
import {NotificationBadgeSettings} from './badge'

const ModalInner = styled.div``

// platform type is GCM with an attribute of AuthenticationMethod=Token
// however in the UI want to make it clear by calling it a different name
const FCM_HTTP_V1 = 'FCM HTTP V1'

const displayPlatformToPlatformMap = (platform: DisplayPlatform) =>
  platform === FCM_HTTP_V1 ? 'GCM' : platform

const platformOptions = [
  {
    value: 'APNS',
    displayValue: 'iOS'
  },
  {
    value: 'APNS_SANDBOX',
    displayValue: 'iOS Sandbox'
  },
  {
    value: 'GCM',
    displayValue: 'Android API Key' // TODO add (Legacy) to this when we remove FCM from preview
  },
  {
    value: FCM_HTTP_V1, // display-only option (GCM under the hood)
    displayValue: FCM_HTTP_V1
  }
] as const

const overflow =
  (
    edit: (platform: NotificationPlatform) => void,
    del: (platform: NotificationPlatform) => void,
    toggleEnabled: (platform: NotificationPlatform) => void,
    canEdit: boolean
  ) =>
  (platform: NotificationPlatform, closeDropdown: () => void) =>
    (
      <>
        <DropdownBody>
          <DropdownItem disabled={!canEdit}>
            <a
              onClick={() => {
                edit(platform)
                closeDropdown()
              }}
            >
              {platform.arn ? 'Edit' : 'Setup'}
            </a>
          </DropdownItem>
          <DropdownItem disabled={!canEdit}>
            <DeleteLink
              onClick={() => {
                toggleEnabled(platform)
                closeDropdown()
              }}
            >
              {platform.enabled ? 'Disable' : 'Enable'}
            </DeleteLink>
          </DropdownItem>
          {!platform.enabled && (
            <DropdownItem disabled={!canEdit}>
              <DeleteLink
                onClick={() => {
                  del(platform)
                  closeDropdown()
                }}
              >
                Delete
              </DeleteLink>
            </DropdownItem>
          )}
        </DropdownBody>
        <DropdownFooter>
          <OverflowFooter id={platform.id} created={platform.created} />
        </DropdownFooter>
      </>
    )

type DisplayPlatform = NotificationPlatform['platform'] | typeof FCM_HTTP_V1
type DisplayNotificationPlatform = NotificationPlatform & {
  /** display name for visual-only use */
  displayPlatform: DisplayPlatform
}

const isValid = (
  input: NotificationPlatform,
  editingExisting?: boolean
): boolean => {
  // this validates against the attribute excluding id
  // if we are editing an existing we won't have the API key (apiKey) within input,
  // so just add a placeholder for that field
  const data = {...input, ...(editingExisting ? {apiKey: 'dummy'} : {})}
  return BaseNotificationPlatformCodec.is(data)
}

export const Notifications = ({
  environment
}: {
  environment: EnvironmentNode
}) => {
  const toaster = useToast()
  const api = getApi({organisationId: environment.organisationId, getToken})
  const analytics = useAnalytics()
  const toast = useToast()

  const canEdit = environment.hasPermission(
    PermissionResource.SnsPlatform,
    PermissionAction.Edit
  )

  const canView = environment.hasPermission(
    PermissionResource.SnsPlatform,
    PermissionAction.View
  )
  const [editingPlatform, setEditingPlatform] =
    useState<Partial<NotificationPlatform>>()

  const [fetchPlatforms, {list: platformResponse = [], loading, errors}] =
    useListLazy<PlatformResponse>(
      `/workbench/v1/${environment.id}/notification-platforms`,
      environment.organisationId
    )

  useEffect(() => {
    if (errors) {
      toast(errors.map(e => e.title).join(', '), 'error')
      notify(
        new Error(
          `Something went wrong fetching notification platforms (${errors})`
        )
      )
    }
  }, [errors, toast])

  useEffect(() => {
    if (canView) {
      fetchPlatforms()
    }
  }, [canView, fetchPlatforms])

  // @ts-ignore bit of munging to make the api model line up with the domain representation without having to duplicate all the things
  const platforms: DisplayNotificationPlatform[] = platformResponse.map(p => ({
    // override this display name with the new one
    displayPlatform:
      p.attributes.platform === 'GCM' &&
      p.attributes.attributes?.AuthenticationMethod === 'Token'
        ? FCM_HTTP_V1
        : p.attributes.platform,
    id: p.id,
    platform: p.attributes.platform,
    appId: p.attributes.appId,
    arn: p.attributes.arn,
    created: p.attributes.created,
    enabled: p.attributes.enabled,
    // android specific
    ...(p.attributes.attributes?.AuthenticationMethod === 'Key'
      ? {apiKey: undefined} // we don't have the token in the response, just set it empty
      : p.attributes.attributes?.AuthenticationMethod === 'Token'
      ? {token: undefined} // we don't have the token in the response, just set it empty
      : {}),
    platformProperties: {
      ...p.attributes.properties
    }
  }))

  const savePlatform = useCallback(
    async (save: NotificationPlatform) => {
      const method = save.arn ? 'put' : 'post'

      const {status, body} = await api(
        method,
        `/workbench/v1/${environment.id}/notification-platforms`,
        omit(save, 'displayName')
      )

      if (status < 400) {
        toaster('Platform saved')
        await fetchPlatforms()
        setEditingPlatform(undefined)
        wipeWorkingPlatformData()
        analytics.track({
          event:
            method === 'put' ? 'Edited configuration' : 'Added configuration',
          properties: {
            org_id: environment.organisationId,
            environment_id: environment.id,
            category: 'configuration>sdk>notifications'
          }
        })
      } else {
        if (body.match(/duplicate/)) {
          if (body.match('/sns_platform_arn_key/')) {
            return toaster(
              // AFAIK This will only happen when running against localstack in development mode as localstack doesn't generate unique ARN values
              'Unable to save platform, an existing SNS ARN exists',
              'error'
            )
          }
          return toaster(
            'Unable to save platform, please ensure values are unique',
            'error'
          )
        }

        if (body.match(/App Id must be unique between APNS_SANDBOX and APNS/)) {
          return toaster(
            'App Id must be unique between APNS_SANDBOX and APNS platforms',
            'error'
          )
        }

        return toaster('Unable to save platform', 'error')
      }
    },
    [
      api,
      environment.id,
      environment.organisationId,
      toaster,
      fetchPlatforms,
      analytics
    ]
  )

  const deletePlatform = useCallback(
    async (platform: NotificationPlatform) => {
      if (window.confirm('Are you sure you want to delete this platform?')) {
        await api(
          'delete',
          `/workbench/v1/${environment.id}/notification-platforms`,
          {
            platform: platform.platform,
            appId: platform.appId
          }
        )
        fetchPlatforms()
        analytics.track({
          event: 'Edited configuration',
          properties: {
            org_id: environment.organisationId,
            environment_id: environment.id,
            category: 'configuration>sdk>notifications'
          }
        })
      }
    },
    [api, environment.id, environment.organisationId, fetchPlatforms, analytics]
  )

  const togglePlatformEnabled = useCallback(
    async (platform: NotificationPlatform) => {
      await api(
        'put',
        `/workbench/v1/${environment.id}/notification-platform-enabled`,
        {
          enabled: !platform.enabled,
          id: platform.id
        }
      )
      fetchPlatforms()
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>sdk>notifications'
        }
      })
    },
    [api, environment.id, environment.organisationId, fetchPlatforms, analytics]
  )

  const platformColumns: ListColumns<DisplayNotificationPlatform> = [
    {
      id: 'id',
      accessor: r => (
        <>
          {isIOS(r.platform) && (
            <Icon name="apple" style={{marginRight: '10px'}} />
          )}
          {isAndroid(r.platform) && (
            <Icon name="android" style={{marginRight: '10px'}} />
          )}
          {canEdit ? (
            <a
              style={{marginRight: '-10px'}}
              onClick={() => setEditingPlatform(r)}
            >
              {r.appId}
            </a>
          ) : (
            <strong style={{marginRight: '10px'}}>{r.appId}</strong>
          )}
          <Tag appearance={r.enabled ? 'green' : 'light'}>
            {r.enabled ? (r.arn ? 'Active' : 'Inactive') : 'Disabled'}
          </Tag>
        </>
      )
    },
    {
      id: 'platform',
      accessor: r => r.displayPlatform
    }
  ]

  useEffect(() => {
    analytics.track({
      event: 'Viewed notifications configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>sdk>notifications'
      }
    })
  }, [analytics, environment])

  return (
    <>
      <Section>
        <Helmet>
          <title>Notifications</title>
        </Helmet>
        <header>
          <IconHeading>
            <Icon name="bell" /> Notifications
          </IconHeading>

          <nav>
            <Button disabled={!canEdit} onClick={() => setEditingPlatform({})}>
              New platform
            </Button>
          </nav>
        </header>

        <SectionInfo>
          <p>Enable Atomic to send push notifications in your apps</p>
        </SectionInfo>

        <SectionContent>
          <List
            columns={platformColumns}
            rows={platforms}
            loading={loading}
            bleed={'#f2f2f2'}
            renderOverflow={overflow(
              setEditingPlatform,
              deletePlatform,
              togglePlatformEnabled,
              canEdit
            )}
            renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
            displayHeader={false}
          />
        </SectionContent>
        {!!editingPlatform && (
          <NotificationModal
            data={editingPlatform}
            onClose={() => setEditingPlatform(undefined)}
            onSave={savePlatform}
          />
        )}
      </Section>
      <NotificationBadgeSettings environment={environment} />
    </>
  )
}

/** Apply some defaults when switching between platforms */
const getPlatformPropertyDefaults = (
  platform: (typeof platformOptions)[number]['value']
): Partial<NotificationPlatform> => {
  switch (platform) {
    case 'APNS':
    case 'APNS_SANDBOX':
      return {
        platformProperties: undefined
      }
    case 'GCM':
      return {
        apiKey: '',
        platformProperties: {
          sendDataOnlyNotifications: false
        }
      }
    case FCM_HTTP_V1:
      return {
        token: '',
        platformProperties: {
          sendDataOnlyNotifications: false
        }
      }
  }
}

const wipeWorkingPlatformData = () => {
  Object.keys({...sessionStorage})
    .filter(k => k.startsWith('platformConfig:'))
    .forEach(k => {
      sessionStorage.removeItem(k)
    })
}

const restoreWorkingPlatform = (value: string) => {
  return JSON.parse(sessionStorage.getItem(`platformConfig:${value}`) ?? '{}')
}

const saveWorkingPlatform = (platformConfig: {displayPlatform?: string}) => {
  if (platformConfig.displayPlatform)
    sessionStorage.setItem(
      `platformConfig:${platformConfig.displayPlatform}`,
      JSON.stringify(platformConfig)
    )
}

const NotificationModal = ({
  data,
  onClose,
  onSave
}: {
  data: Partial<NotificationPlatform>
  onClose: () => void
  onSave: (save: DisplayNotificationPlatform) => void
}) => {
  const [saving, setSaving] = useState<boolean>()
  const [
    notificationPlatform,
    setNotificationPlatform,
    notificationPlatformChanged,
    resetNotificationPlatform
  ] = useStateChanged<Partial<DisplayNotificationPlatform>>(data)

  const editingExisting = notificationPlatform && !!notificationPlatform.arn
  const wrappedHandleClose = () => {
    if (
      !notificationPlatformChanged ||
      confirm(
        'You have unsaved changes - are you sure you want to discard them?'
      )
    ) {
      setSaving(false)
      resetNotificationPlatform()
      wipeWorkingPlatformData()
      onClose()
    }
  }

  const platformDisplay = notificationPlatform
    ? platformOptions.find(p => p.value === notificationPlatform.platform)
        ?.displayValue
    : undefined

  return (
    <Modal handleClose={wrappedHandleClose} title="Platform configuration">
      <ModalInner>
        <form
          onSubmit={e => {
            e.preventDefault()

            const validPlatform = isValid(
              notificationPlatform as NotificationPlatform,
              editingExisting
            )

            if (validPlatform) {
              onSave(notificationPlatform as DisplayNotificationPlatform)
            }
          }}
        >
          <div style={{marginBottom: '20px'}}>
            {!editingExisting && (
              <Select
                label="Select platform..."
                upLabel="Platform"
                value={notificationPlatform.displayPlatform ?? ''}
                options={platformOptions.map(p => ({
                  text: p.displayValue,
                  value: p.value
                }))}
                onChange={value => {
                  if (!value) return // help with types
                  setNotificationPlatform(platformConfig => {
                    // save the current platform config to session storage in case they switch back
                    saveWorkingPlatform(platformConfig)
                    const savedForPlatform = restoreWorkingPlatform(value)
                    return {
                      // when switching between platforms override invalid props and set defaults
                      ...getPlatformPropertyDefaults(value),
                      ...savedForPlatform,
                      displayPlatform: value, // keep this
                      // need to map display back underlying type
                      platform: displayPlatformToPlatformMap(value),
                      appId: platformConfig?.appId ?? '' // always keep the app id regardless of whats in session storage
                    }
                  })
                }}
              />
            )}
            {!!editingExisting && (
              <TextInput
                label="Platform"
                value={platformDisplay}
                readOnly={true}
                onChange={() => undefined}
              />
            )}
          </div>
          <div style={{marginBottom: '20px'}}>
            <TextInput
              label="Enter app ID..."
              upLabel="App ID"
              value={notificationPlatform.appId ?? ''}
              readOnly={editingExisting}
              autoComplete="new-password"
              onChange={value => {
                setNotificationPlatform(editing => {
                  return editing
                    ? {
                        ...editing,
                        appId: value
                      }
                    : editing
                })
              }}
            />
          </div>
          {notificationPlatform.platform &&
            isIOS(notificationPlatform.platform) && (
              <IosForm
                attributes={notificationPlatform as PlatformAttributes}
                setAttributes={setNotificationPlatform}
              />
            )}
          {notificationPlatform.platform &&
            isAndroid(notificationPlatform.platform) && (
              <AndroidForm
                editingExisting={editingExisting ?? false}
                attributes={
                  notificationPlatform as
                    | PlatformAndroidToken
                    | PlatformAndroidApiKey
                }
                update={setNotificationPlatform}
              />
            )}
          <div style={{marginTop: '30px'}}>
            <ButtonGroup>
              <Button
                type="submit"
                size="large"
                disabled={
                  !notificationPlatformChanged ||
                  !isValid(
                    notificationPlatform as NotificationPlatform,
                    editingExisting
                  ) ||
                  saving
                }
              >
                {saving ? 'Saving' : 'Save'}
              </Button>
              <Button
                onClick={() => {
                  resetNotificationPlatform()
                  onClose()
                }}
                appearance="tint"
                size="large"
              >
                Cancel
              </Button>
            </ButtonGroup>
          </div>
        </form>
      </ModalInner>
    </Modal>
  )
}
